import {
  GetRecommendationByIdApiResponse,
  GetSelfAssessmentApiResponse,
  SearchCriteria,
} from '@/api'
import { Column, ShowWhen, Typography } from '@/components'
import { config } from '@/config'
import { ThemeColor } from '@/enums'
import { useComparisonTable } from '@/pages/RecommendationPage/hooks/useComparisonTable'
import { useRiskAdditionalInfo } from '@/pages/RecommendationPage/hooks/useRiskAdditionalInfo'
import { useContactModal, useSnackbar } from '@/providers'
import { useAttributes } from '@/services/attributes'
import { useLocationTypes } from '@/services/locationTypes'
import { theme } from '@/theme'
import { useIsMobile } from '@/utils'
import { formatRiskAddress } from '@/utils/address'
import { useCategoryTranslations } from '@/utils/hooks/useCategoryTranslations'
import { ContactBanner, Skeleton, ComparisonTableOld } from '@surein/ui'
import { BadgeCheck, MessageCircleMore, User2 } from 'lucide-react'
import { memo, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CompaniesTrust } from './CompaniesTrust'
import { Faqs } from './Faqs'
import { IndependentBroker } from './IndependentBroker'
import { Products } from './components/Products/Products'
import { TestimonialSection } from './TestimonialSection'
import { TopActions } from './TopActions'
import { WhatIsProtected } from './WhatIsProtected'
import { prepareRecommendation, useMessages } from './helpers'
import {
  CollapsibleAdvisorContentWrapper,
  ComparisonTableCard,
  Container,
  FaqsWrapper,
  FlexWrapper,
  RecommendationTitle,
  RiskInsuredSpacer,
  RiskInsuredTypography,
} from './styles'
import { RecommendationLoader } from './components/RecommendationLoader/RecommendationLoader'
import { useIsRecommendationPriceLoading } from '@/services'
import { RelatedRecommendationHint } from './components/RelatedRecommendationHint/RelatedRecommendationHint'
import { RecommendationCustomization } from './components/RecommendationCustomization/RecommendationCustomization'
import { RecommendationFloatingCTA } from './components/RecommendationFloatingCTA/RecommendationFloatingCTA'
import { useRecommendationFloatingCta } from './components/RecommendationFloatingCTA/useRecommendationFloatingCTA'
import { isAnyProductMismatched } from './components/Products/helpers'

const RisksInsured = ({
  categoryId,
  assessmentValues = {},
  searchCriteria,
}: {
  categoryId: string
  assessmentValues?: {
    businessType?: {
      locationType: string
      locationSubtypes: string[]
    }
    companyDetails?: {
      address: {
        street: string
        streetNumber?: string | undefined
        streetNr?: string | undefined
        postalCode: string
        city: string
      }
    }
    locationDetails?: {
      address: {
        street: string
        streetNumber?: string | undefined
        streetNr?: string | undefined
        postalCode: string
        city: string
      }
    }
  }
  searchCriteria?: SearchCriteria
}) => {
  const { t } = useTranslation()
  const {
    data: locationTypesData,
    isLoading: locationTypesLoading,
  } = useLocationTypes()
  // As discussed with the BE, this component is not meant to last long
  // and will be replaced by a more dynamic BE handling in the future

  // @ts-expect-error TODO: fix this - no idea why this could be an array
  if (assessmentValues.length === 0) return null

  // The first non-'other' entry is treated as the location type and the
  // rest as subtypes; this ordering is a contract with how the search bar
  // and backend populate searchCriteria.businessType.
  const rawSearchCriteriaBusinessType = searchCriteria?.businessType ?? []
  const [
    searchCriteriaLocationType,
    ...searchCriteriaLocationSubtypes
  ] = rawSearchCriteriaBusinessType.filter((type) => type !== 'other')
  const searchCriteriaSelectedOnlyOther =
    !searchCriteriaLocationType &&
    rawSearchCriteriaBusinessType.includes('other')

  const businessType = searchCriteriaLocationType
    ? {
        locationType: searchCriteriaLocationType,
        locationSubtypes: searchCriteriaLocationSubtypes,
      }
    : searchCriteriaSelectedOnlyOther
    ? { locationType: 'other', locationSubtypes: [] }
    : assessmentValues?.businessType

  const getLocationLabel = (locationType: string) =>
    locationType === 'other'
      ? searchCriteria?.businessTypeOtherSpecification ?? undefined
      : locationTypesData?.getLocationLabel(locationType)

  if (categoryId === 'CO' && !!assessmentValues?.locationDetails?.address) {
    return (
      <RiskInsuredTypography variant="p1Body">
        <Trans
          i18nKey="recommendationRisksInsured"
          values={{
            risk: formatRiskAddress(assessmentValues.locationDetails.address),
          }}
        />
      </RiskInsuredTypography>
    )
  }

  if (categoryId === 'CO' && !!assessmentValues?.companyDetails?.address) {
    return (
      <RiskInsuredTypography variant="p1Body">
        <Trans
          i18nKey="recommendationRisksInsured"
          values={{
            risk: formatRiskAddress(assessmentValues.companyDetails.address),
          }}
        />
      </RiskInsuredTypography>
    )
  }

  if (
    businessType?.locationType &&
    businessType?.locationSubtypes?.length > 0
  ) {
    return locationTypesLoading ? (
      <Skeleton center count={2} withParagraphEnding />
    ) : (
      <RiskInsuredTypography variant="p1Body">
        {t('analyzedOffers')}
        <br />
        <Trans
          count={businessType.locationSubtypes.length}
          i18nKey="recommendationRisksInsuredWithSubtypes"
          values={{
            risk: getLocationLabel(businessType.locationType),
            subtypes: businessType.locationSubtypes
              .map((item) => locationTypesData?.getLocationLabel(item))
              .join(', '),
          }}
        />
      </RiskInsuredTypography>
    )
  }

  if (businessType?.locationType) {
    return locationTypesLoading ? (
      <Skeleton center count={2} withParagraphEnding />
    ) : (
      <RiskInsuredTypography variant="p1Body">
        {t('analyzedOffers')}
        <br />
        <Trans
          i18nKey="recommendationRisksInsured"
          values={{
            risk: getLocationLabel(businessType.locationType),
          }}
        />
      </RiskInsuredTypography>
    )
  }

  return null
}

const collapsibleAdvisorContentIcons = [User2, BadgeCheck, MessageCircleMore]

interface CollapsibleAdvisorContentProps {
  advisorName: string
}

const CollapsibleAdvisorContent = ({
  advisorName,
}: CollapsibleAdvisorContentProps) => {
  const { t } = useTranslation()
  const content: string[] = t(
    `contactCarouselContent.${advisorName}.features`,
    {
      returnObjects: true,
    },
  )

  return (
    <Column gap="12px">
      {content.map((item, index) => {
        const Icon = collapsibleAdvisorContentIcons[index]
        return (
          <CollapsibleAdvisorContentWrapper key={item}>
            <Icon color={theme.color[ThemeColor.info1]} size={24} />
            <Typography key={content[index]} variant="p2Body">
              {item}
            </Typography>
          </CollapsibleAdvisorContentWrapper>
        )
      })}
    </Column>
  )
}

const MemoizedRisksInsured = memo(RisksInsured)

interface BasePageProps {
  assessmentValues?: GetSelfAssessmentApiResponse['values']
  data: GetRecommendationByIdApiResponse
  handleFileClick: (file: { fileId: string; name: string }) => void
  noBack?: boolean
  onBack?: () => void
  onChoose: (id: string) => void
  shouldShowRecommendationCustomization: boolean
}

export const BasePage = ({
  assessmentValues,
  data,
  onChoose,
  onBack,
  noBack,
  handleFileClick,
  shouldShowRecommendationCustomization,
}: BasePageProps) => {
  const { t } = useTranslation()
  const snackbars = useSnackbar()
  const messages = useMessages(data.categoryId)
  const {
    isLoading: isLoadingAttributes,
    data: attributesData,
  } = useAttributes()
  const { isMobile } = useIsMobile()
  const { items } = useComparisonTable(data, handleFileClick)

  const { categoryKey } = useCategoryTranslations(data.categoryId)

  const { additionalInfo } = useRiskAdditionalInfo(
    assessmentValues,
    categoryKey,
    data.searchCriteria,
    data.companyFoundedDate,
  )
  const { openContact } = useContactModal()
  const {
    isRecommendationPriceLoading: isRecommendationReloading,
  } = useIsRecommendationPriceLoading(data.recommendationId || '')

  const isPageLoading = isLoadingAttributes || !attributesData

  const { staticPriceListRef, shouldShowCta } = useRecommendationFloatingCta({
    enabled: !isPageLoading,
  })

  useEffect(() => {
    if (isAnyProductMismatched(data)) {
      snackbars.warning(t('recommendations.customization.mismatchToastMessage'))
    }
  }, [data])

  if (isPageLoading) {
    return null
  }

  const labels = [
    {
      label: t('insured_sum'),
      annotationText: t('info.amountInsured'),
    },
    {
      label: t('deductible'),
      annotationText: t('info.deductible'),
    },
    ...(data.visibleAttributes || []).map((attr) => ({
      label: attributesData.getAttributeLabel(attr),
      annotationText: attributesData.getAttributeDescription(attr),
    })),
    {
      label: t('insurance_details'),
    },
  ]

  return (
    <Container>
      <FlexWrapper>
        <TopActions noBack={noBack} onBack={onBack} />
        <Column gap="16px">
          <RecommendationTitle data-testid="recommendation-title">
            <Typography bold breakWord variant="h2">
              {messages.offerTitle}
            </Typography>
          </RecommendationTitle>
          <CompaniesTrust />
        </Column>
        <RiskInsuredSpacer>
          <MemoizedRisksInsured
            assessmentValues={assessmentValues}
            categoryId={data.categoryId}
            searchCriteria={data.searchCriteria}
          />
        </RiskInsuredSpacer>
        <RelatedRecommendationHint
          relatedRecommendationIds={data.relatedRecommendationIds}
        />
        <Products
          ref={staticPriceListRef}
          recommendationData={prepareRecommendation(data)}
          onChoose={onChoose}
          shouldShowRecommendationCustomization={
            shouldShowRecommendationCustomization
          }
          isRecommendationReloading={isRecommendationReloading}
        />
        <RecommendationCustomization
          recommendationData={data}
          shouldShowRecommendationCustomization={
            shouldShowRecommendationCustomization
          }
        />
        <ComparisonTableCard className="px-4" id="details" variant="borderless">
          <ComparisonTableOld
            additionalInfo={additionalInfo}
            items={items}
            labels={labels}
            onCTAClick={(selectedItem) => onChoose(selectedItem.id)}
            tableLabel={t('recommendations.title')}
            shouldShowCTA={isMobile}
          />
        </ComparisonTableCard>
        <Column gap="16px">
          <WhatIsProtected categoryId={data.categoryId} />
          <ContactBanner
            buttonText={t('recommendations.contactBannerBtn')}
            collapsibleContent={
              <CollapsibleAdvisorContent advisorName={'daniel'} />
            }
            collapsibleTriggerText={t(
              'recommendations.contactBannerCollapsibleTriggerText',
            )}
            imageUrl={`${config.cdn}/chatblock.png`}
            onButtonClick={openContact}
            subtitle={t('recommendations.contactBannerSubtitle')}
            title={t('recommendations.contactBannerTitle')}
          />
        </Column>

        <IndependentBroker />

        <TestimonialSection isMobile={isMobile} />
      </FlexWrapper>
      <FaqsWrapper>
        <Faqs />
      </FaqsWrapper>
      <RecommendationLoader isVisible={isRecommendationReloading} />
      <ShowWhen when={!isMobile}>
        <RecommendationFloatingCTA
          isRecommendationReloading={isRecommendationReloading}
          recommendationData={prepareRecommendation(data)}
          visible={shouldShowCta}
          onCTAClick={onChoose}
        />
      </ShowWhen>
    </Container>
  )
}
